// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import { Lift } from "../../types/dataTypes";
import { JudgeVoteValue } from "../judging/resolveJudgeVotes";
import { PlatformLiveAttempt } from "./platformLiveProtocol";
import { recordAttemptAriaLabel, recordAttemptLabel, recordCategoryDataAttributes } from "../records/recordCategories";

import styles from "./PlatformLiveAttemptHistory.module.scss";

const joinClassNames = (...classNames: Array<string | false | null | undefined>): string => {
  return classNames.filter((className): className is string => typeof className === "string").join(" ");
};

export const getLiftLabel = (lift: Lift): string => {
  if (lift === "S") {
    return "Sentadilla";
  }
  if (lift === "B") {
    return "Banco Plano";
  }
  return "Peso Muerto";
};

export interface PlatformLiveAttemptHistoryProps {
  attempt: PlatformLiveAttempt | null;
  dimmed?: boolean;
  revealActive?: boolean;
  revealAttemptKey?: string | null;
  revealedResult?: JudgeVoteValue | null;
  variant?: "spectator" | "judge";
  /** Sin panel propio: el espectador lo apila dentro de su panel, entre los otros movimientos. */
  embedded?: boolean;
}

const PlatformLiveAttemptHistory = ({
  attempt,
  dimmed = false,
  revealActive = false,
  revealAttemptKey = null,
  revealedResult = null,
  variant = "spectator",
  embedded = false,
}: PlatformLiveAttemptHistoryProps): React.ReactElement | null => {
  if (attempt === null) {
    return null;
  }

  const fourth = attempt.attemptSeries?.[3];
  const fourthAttemptRequested =
    attempt.attempt === 4 || (fourth !== undefined && (fourth.weightKg > 0 || fourth.status !== "pending"));
  const attemptCount = fourthAttemptRequested ? 4 : 3;

  const series = Array.from({ length: attemptCount }, (_, index) => {
    const published = attempt.attemptSeries?.[index];
    const item = published || {
      weightKg: index === attempt.attempt - 1 ? attempt.weightKg : 0,
      status: "pending" as const,
    };
    if (
      revealActive &&
      revealAttemptKey === attempt.key &&
      index === attempt.attempt - 1 &&
      (revealedResult === "good" || revealedResult === "noLift")
    ) {
      return { ...item, status: revealedResult };
    }
    return item;
  });

  const lift = attempt.athleteMeta?.lift || attempt.lift;

  return (
    <section
      className={joinClassNames(
        styles.attemptHistory,
        variant === "spectator" && styles.attemptHistorySpectator,
        variant === "judge" && styles.attemptHistoryJudge,
        dimmed && styles.attemptHistoryDimmed,
        embedded && styles.attemptHistoryEmbedded,
      )}
      aria-label="Historial de intentos"
    >
      <h2>{getLiftLabel(lift).toUpperCase()}</h2>
      <table>
        <thead>
          <tr>
            {series.map((_item, index) => (
              <th key={index}>{index + 1}º INTENTO</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {series.map((item, index) => {
              const weight = item.weightKg === 0 ? "—" : `${item.weightKg} KG`;
              const statusLabel = item.status === "good" ? "válido" : item.status === "noLift" ? "nulo" : "pendiente";
              const isRecord = item.recordAttempt === true;
              const recordLabel = recordAttemptLabel(item.recordCategory);
              const statusClass =
                item.status === "good"
                  ? styles.attemptGood
                  : item.status === "noLift"
                    ? styles.attemptNoLift
                    : styles.attemptPending;
              return (
                <td
                  key={index}
                  className={joinClassNames(statusClass, index === attempt.attempt - 1 && styles.attemptActive)}
                  aria-current={index === attempt.attempt - 1 ? "true" : undefined}
                  aria-label={`${index + 1}º intento: ${weight}, ${statusLabel}${
                    isRecord ? `, ${recordLabel.toLocaleLowerCase("es")}` : ""
                  }`}
                  {...(isRecord ? recordCategoryDataAttributes(item.recordCategory) : {})}
                >
                  <div className={styles.attemptHistoryCell}>
                    <span>{weight}</span>
                    {isRecord ? (
                      <span
                        className={styles.attemptHistoryRecordBadge}
                        aria-label={recordAttemptAriaLabel(item.recordCategory)}
                        {...recordCategoryDataAttributes(item.recordCategory)}
                      >
                        {recordLabel}
                      </span>
                    ) : null}
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </section>
  );
};

export default PlatformLiveAttemptHistory;
